import { FormEvent, useEffect, useState } from "react";
import { Box, Grid, Paper, Typography } from "@mui/material";
import NavigationHeader from "components/NavigationHeader";
import { DashboardStackCustom } from "components/StackCustom/styles";
import { useNavigation } from "contexts/notificationsContext";
import { alterarSenha, IMinhaConta, mensagemDeErro, obterMinhaConta } from "services/auth";
import { AvisoErro, RegraSenhaVisivel } from "pages/Login/components/CartaoAcesso";
import { senhaAtendeRegra } from "pages/Login/regraSenha";

/**
 * Minha Conta (plano de 07/10/2026, B3): dados do usuário logado e troca da própria senha, sem passar
 * pelo MOF antigo. Gabarito: site/src/pages/MinhaConta do legado.
 */
export function MinhaConta() {
  const { openNotification } = useNavigation();
  const [conta, setConta] = useState<IMinhaConta | null>(null);
  const [senhaAtual, setSenhaAtual] = useState("");
  const [novaSenha, setNovaSenha] = useState("");
  const [confirmacao, setConfirmacao] = useState("");
  const [erro, setErro] = useState<string | null>(null);
  const [gravando, setGravando] = useState(false);

  useEffect(() => {
    obterMinhaConta()
      .then(setConta)
      .catch((e: any) => {
        // 401: o interceptor da api já mandou para o login.
        if (e?.status !== 401) setErro(mensagemDeErro(e, "Não foi possível carregar os dados da sua conta."));
      });
  }, []);

  const handleSubmit = async (evento: FormEvent) => {
    evento.preventDefault();
    setErro(null);

    if (!senhaAtual) {
      setErro("Informe a senha atual.");
      return;
    }
    if (!senhaAtendeRegra(novaSenha)) {
      setErro("A nova senha não atende à regra abaixo.");
      return;
    }
    if (novaSenha !== confirmacao) {
      setErro("A confirmação não confere com a nova senha.");
      return;
    }

    setGravando(true);
    try {
      await alterarSenha(senhaAtual, novaSenha);
      setSenhaAtual("");
      setNovaSenha("");
      setConfirmacao("");
      openNotification({ message: "Senha alterada com sucesso.", type: "success" });
    } catch (e: any) {
      // 401: o interceptor da api já mandou para o login.
      if (e?.status === 401) return;
      setErro(mensagemDeErro(e, "Não foi possível alterar a senha. Tente novamente."));
    } finally {
      setGravando(false);
    }
  };

  const linhaDado = (rotulo: string, valor?: string) => (
    <Grid item xs={12} sm={6}>
      <Typography variant="caption" color="text.secondary">
        {rotulo}
      </Typography>
      <Typography variant="body1">{valor ?? "—"}</Typography>
    </Grid>
  );

  return (
    <DashboardStackCustom>
      <NavigationHeader title="MINHA CONTA" label="Dados de acesso" hasBackButton={false} />

      <Paper elevation={3}>
        <Box sx={{ p: 3 }}>
          <Grid container spacing={2}>
            {linhaDado("Nome", conta?.nome)}
            {linhaDado("E-mail", conta?.email)}
            {linhaDado("Empresa", conta?.empresa)}
            {linhaDado("Tipo de usuário", conta?.tipo)}
          </Grid>
        </Box>
      </Paper>

      <Paper elevation={3} sx={{ mt: 2 }}>
        <Box sx={{ p: 3, maxWidth: 480 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Alterar senha
          </Typography>
          <form onSubmit={handleSubmit} noValidate>
            <AvisoErro mensagem={erro} />
            <div className="mb-3">
              <label className="form-label" htmlFor="minha-conta-senha-atual">
                Senha atual
              </label>
              <input
                id="minha-conta-senha-atual"
                type="password"
                className="form-control"
                autoComplete="current-password"
                value={senhaAtual}
                onChange={(e) => setSenhaAtual(e.target.value)}
              />
            </div>
            <div className="mb-3">
              <label className="form-label" htmlFor="minha-conta-nova-senha">
                Nova senha
              </label>
              <input
                id="minha-conta-nova-senha"
                type="password"
                className="form-control"
                autoComplete="new-password"
                value={novaSenha}
                onChange={(e) => setNovaSenha(e.target.value)}
              />
            </div>
            <RegraSenhaVisivel senha={novaSenha} />
            <div className="mb-3">
              <label className="form-label" htmlFor="minha-conta-confirmacao">
                Confirme a nova senha
              </label>
              <input
                id="minha-conta-confirmacao"
                type="password"
                className="form-control"
                autoComplete="new-password"
                value={confirmacao}
                onChange={(e) => setConfirmacao(e.target.value)}
              />
            </div>
            <button type="submit" className="btn btn-mof" disabled={gravando}>
              {gravando ? "SALVANDO..." : "ALTERAR SENHA"}
            </button>
          </form>
        </Box>
      </Paper>
    </DashboardStackCustom>
  );
}
